import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  ArrowLeft, Camera, Save, User, Plus, Trash2, AlertCircle, Phone, Mail, 
  Activity, Heart, MapPin, Shield, CheckCircle, X, Eye, EyeOff, Calendar,
  Clock, Users, FileText, Briefcase, Home, Globe, Languages, UserCheck,
  Stethoscope, Building, Contact, Settings, Lock, ChevronRight, ChevronDown
} from 'lucide-react';
import { useUI } from '../../contexts/UIContext';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { validateProfileData, sanitizeInput } from '../../utils/validation';
import { formatPhoneNumber, isValidPhoneNumber, isValidEmail } from '../../utils/formatters';
import AddressInput from '../../components/AddressInput';
import InternationalAddressInput from '../../components/InternationalAddressInput';
import WorldLocationSelector from '../../components/WorldLocationSelector';
import HealthManager from '../../components/HealthManager';

// Preload essential styles for performance optimization
const preloadIdentityStyles = () => {
  if (typeof document !== 'undefined' && !document.getElementById('identityEditStyles')) {
    const style = document.createElement('style');
    style.id = 'identityEditStyles';
    style.textContent = `
      .identity-edit-container {
        contain: layout style;
        will-change: transform;
        backface-visibility: hidden;
        transform: translateZ(0);
      }
      
      .field-input {
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        border: 2px solid transparent;
        background-clip: padding-box;
      }
      
      .field-input:focus {
        border-color: #3B82F6;
        box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.15);
        outline: none;
      }
      
      .field-input.error {
        border-color: #EF4444;
        box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.15);
      }
      
      .field-input.success {
        border-color: #10B981;
        box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.15);
      }
      
      .tab-nav {
        position: sticky;
        top: 0;
        z-index: 20;
        backdrop-filter: blur(12px) saturate(180%);
        -webkit-backdrop-filter: blur(12px) saturate(180%);
      }
      
      .tab-content {
        animation: fadeSlideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      }
      
      @keyframes fadeSlideIn {
        from { 
          opacity: 0; 
          transform: translateY(12px);
        }
        to { 
          opacity: 1; 
          transform: translateY(0);
        }
      }
      
      .identity-card {
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        border: 1px solid transparent;
        backdrop-filter: blur(8px);
      }
      
      .identity-card:hover {
        border-color: #3B82F6;
        box-shadow: 0 8px 25px rgba(59, 130, 246, 0.15);
        transform: translateY(-2px);
      }
      
      .save-success {
        animation: slideInSuccessNotification 0.6s cubic-bezier(0.4, 0, 0.2, 1);
      }
      
      @keyframes slideInSuccessNotification {
        from { 
          transform: translateY(-100%) scale(0.9); 
          opacity: 0; 
        }
        to { 
          transform: translateY(0) scale(1); 
          opacity: 1; 
        }
      }
      
      .form-section {
        scroll-margin-top: 80px;
      }
      
      .array-item {
        animation: slideInItem 0.3s ease;
      }
      
      @keyframes slideInItem {
        from { opacity: 0; transform: translateX(-20px); }
        to { opacity: 1; transform: translateX(0); }
      }
    `;
    document.head.appendChild(style);
  }
};

// Preload styles immediately
preloadIdentityStyles();

// Enhanced input component with validation feedback
const ValidatedInput = React.memo(({ 
  label, 
  name, 
  type = 'text', 
  value, 
  onChange, 
  error, 
  placeholder, 
  isDarkMode, 
  required = false,
  maxLength,
  pattern,
  ...props 
}) => {
  const hasError = error && error.length > 0;
  const hasValue = value && value.length > 0;
  
  return (
    <div className="space-y-1">
      <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        maxLength={maxLength}
        pattern={pattern}
        className={`field-input w-full p-3 rounded-xl transition-all duration-200 
          ${hasError 
            ? 'error border-red-500' 
            : hasValue 
              ? 'success border-green-500' 
              : `border-gray-300 ${isDarkMode ? 'border-gray-600' : ''}`
          } 
          ${isDarkMode 
            ? 'bg-gray-700 text-white placeholder-gray-400' 
            : 'bg-white text-gray-900 placeholder-gray-500'
          }`}
        {...props}
      />
      {hasError && (
        <p className="text-red-500 text-xs mt-1 flex items-center">
          <AlertCircle size={12} className="mr-1" />
          {error}
        </p>
      )}
      {maxLength && (
        <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {value.length}/{maxLength} karakter
        </p>
      )}
    </div>
  );
});

// Array field management component
const ArrayFieldManager = React.memo(({ 
  title, 
  items, 
  onAdd, 
  onRemove, 
  onUpdate, 
  placeholder, 
  isDarkMode, 
  addButtonColor = 'blue',
  emptyMessage,
  t
}) => {
  const [newItem, setNewItem] = useState('');
  
  const handleAdd = () => {
    if (newItem.trim()) {
      onAdd(newItem.trim());
      setNewItem('');
    }
  };
  
  const colorClasses = {
    blue: 'bg-blue-600 hover:bg-blue-700',
    red: 'bg-red-600 hover:bg-red-700',
    green: 'bg-green-600 hover:bg-green-700',
    purple: 'bg-purple-600 hover:bg-purple-700',
    orange: 'bg-orange-600 hover:bg-orange-700'
  };
  
  return (
    <div className={`p-4 rounded-xl border identity-card ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
      <div className="flex justify-between items-center mb-4">
        <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          {title}
        </h3>
        <div className="flex space-x-2">
          <input
            type="text"
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            placeholder={placeholder}
            onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
            className={`px-3 py-2 text-sm rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
          />
          <button
            onClick={handleAdd}
            disabled={!newItem.trim()}
            className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center space-x-1 text-white transition-all
              ${colorClasses[addButtonColor]} disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <Plus size={16} />
            <span>{t('common.add', 'Ekle')}</span>
          </button>
        </div>
      </div>
      
      <div className="space-y-2">
        {items.map((item, index) => (
          <div key={index} className={`array-item flex items-center justify-between p-3 rounded-lg transition-all
            ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-white hover:bg-gray-50'} 
            border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
            <span className={`flex-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{item}</span>
            <button
              onClick={() => onRemove(index)}
              className="p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        ))}
        {items.length === 0 && (
          <p className={`text-center py-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <FileText size={24} className="mx-auto mb-2 opacity-50" />
            {emptyMessage}
          </p>
        )}
      </div>
    </div>
  );
});

// Emergency contact card component
const EmergencyContactCard = React.memo(({ 
  contact, 
  index, 
  onChange, 
  onRemove, 
  isDarkMode, 
  canRemove,
  validationErrors,
  t
}) => {
  const relationshipOptions = [
    { value: 'Aile', label: t('profile.family', 'Aile') },
    { value: 'Eş', label: t('profile.spouse', 'Eş') },
    { value: 'Ebeveyn', label: t('profile.parent', 'Ebeveyn') },
    { value: 'Kardeş', label: t('profile.sibling', 'Kardeş') },
    { value: 'Arkadaş', label: t('profile.friend', 'Arkadaş') },
    { value: 'Doktor', label: t('profile.doctor', 'Doktor') },
    { value: 'Diğer', label: t('profile.other', 'Diğer') }
  ];
  
  // Get errors for this specific contact
  const contactErrors = validationErrors?.[`contact_${index}`] || {};
  
  return (
    <div className={`identity-card p-4 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
      <div className="flex justify-between items-center mb-3">
        <h4 className={`font-medium flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          <Contact size={16} className="mr-2" />
          {t('profile.contact', 'Kişi')} {index + 1}
        </h4>
        {canRemove && (
          <button
            onClick={() => onRemove(index)}
            className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ValidatedInput
          label={t('profile.name', 'İsim')}
          name="name"
          value={contact.name || ''}
          onChange={(e) => onChange(index, 'name', e.target.value)}
          placeholder="Ahmet Yılmaz"
          isDarkMode={isDarkMode}
          required
          error={contactErrors.name}
        />

        <ValidatedInput
          label={t('profile.phone', 'Telefon')}
          name="phone"
          type="tel"
          value={contact.phone || ''}
          onChange={(e) => onChange(index, 'phone', e.target.value)}
          placeholder="+90 555 123 4567"
          isDarkMode={isDarkMode}
          required
          error={contactErrors.phone}
        />

        <div>
          <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {t('profile.relationship', 'Yakınlık')}
          </label>
          <select
            value={contact.relationship || ''}
            onChange={(e) => onChange(index, 'relationship', e.target.value)}
            className={`field-input w-full p-3 rounded-xl ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
          >
            <option value="">{t('common.select', 'Seçiniz')}</option>
            {relationshipOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        <ValidatedInput
          label={t('profile.location', 'Konum')}
          name="location"
          value={contact.location || ''}
          onChange={(e) => onChange(index, 'location', e.target.value)}
          placeholder="İstanbul, Türkiye"
          isDarkMode={isDarkMode}
        />
      </div>
    </div>
  );
});

// Main ProfileEditScreen component
const ProfileEditScreen = () => {  const { navigateToScreen, screenParams } = useUI();
  const { user, updateUserProfile } = useAuth();
  const { currentLanguage, t } = useLanguage();
  // Determine initial section
  const editSection = screenParams?.section || 'general';
  
  console.log('🔄 ProfileEditScreen: Received screenParams:', screenParams);
  console.log('🔄 ProfileEditScreen: Edit section determined as:', editSection);
  
  console.log('🔄 ProfileEditScreen: Received screenParams:', screenParams);
  console.log('🔄 ProfileEditScreen: Edit section determined as:', editSection);
  
  console.log('🔄 ProfileEditScreen: Received screenParams:', screenParams);
  console.log('🔄 ProfileEditScreen: Edit section determined as:', editSection);
  
  // Comprehensive identity data state
  const [formData, setFormData] = useState({
    // Basic Identity
    name: user?.name || '',
    username: user?.username || '',
    bio: user?.bio || '',
    dateOfBirth: user?.dateOfBirth || '',
    gender: user?.gender || '',
    nationality: user?.nationality || '',
    profilePhoto: user?.profilePhoto || null,
    
    // Physical Characteristics
    height: user?.height || '',
    weight: user?.weight || '',
    bloodType: user?.bloodType || '',
    eyeColor: user?.eyeColor || '',
    hairColor: user?.hairColor || '',
      // Contact Information
    phone: user?.phone || '',
    email: user?.email || '',
    secondaryEmail: user?.secondaryEmail || '',    address: user?.address || {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'US'
    },
    
    // Medical Information
    allergies: user?.allergies || [],
    medicalConditions: user?.medicalConditions || [],
    medications: user?.medications || [],
    chronicDiseases: user?.chronicDiseases || [],
    
    // Emergency Contacts
    emergencyContacts: user?.emergencyContacts || [],
    
    // Personal Preferences
    languages: user?.languages || [],
    hobbies: user?.hobbies || [],
    interests: user?.interests || [],
    
    // Additional Notes
    notes: user?.notes || ''
  });  // UI State
  const [isDarkMode] = useState(() => localStorage.getItem('theme') === 'dark');
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [showSaveError, setShowSaveError] = useState(false);
  const [activeTab, setActiveTab] = useState(editSection);
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState(new Set());

  // File upload refs and state
  const fileInputRef = useRef(null);
  const [photoPreview, setPhotoPreview] = useState(user?.profilePhoto || null);

  // Enhanced validation system
  const validateField = useCallback((fieldName, value) => {
    const errors = {};
    
    switch (fieldName) {
      case 'name':
        if (!value?.trim()) {
          errors.name = t('validation.nameRequired', 'İsim gereklidir');
        } else if (value.trim().length < 2) {
          errors.name = t('validation.nameTooShort', 'İsim en az 2 karakter olmalıdır');
        } else if (value.trim().length > 50) {
          errors.name = t('validation.nameTooLong', 'İsim 50 karakterden kısa olmalıdır');
        } else if (!/^[a-zA-ZüğıişöçÜĞIİŞÖÇ\s'-]+$/.test(value.trim())) {
          errors.name = t('validation.nameInvalidChars', 'İsim sadece harf ve boşluk içerebilir');
        }
        break;
        
      case 'username':
        if (value && !/^[a-zA-Z0-9_]{3,20}$/.test(value)) {
          errors.username = t('validation.usernameInvalid', 'Kullanıcı adı 3-20 karakter, harf, rakam ve alt çizgi içerebilir');
        }
        break;
        
      case 'email':
      case 'secondaryEmail':
        if (value && !isValidEmail(value)) {
          errors[fieldName] = t('validation.invalidEmail', 'Geçersiz e-posta formatı');
        }
        break;
        
      case 'phone':
        if (value && !isValidPhoneNumber(value)) {
          errors.phone = t('validation.invalidPhone', 'Geçersiz telefon numarası');
        }
        break;
        
      case 'height':
        if (value && (isNaN(value) || value < 50 || value > 250)) {
          errors.height = t('validation.invalidHeight', 'Boy 50-250 cm arasında olmalıdır');
        }
        break;
        
      case 'weight':
        if (value && (isNaN(value) || value < 20 || value > 300)) {
          errors.weight = t('validation.invalidWeight', 'Kilo 20-300 kg arasında olmalıdır');
        }
        break;
        
      case 'dateOfBirth':
        if (value) {
          const birthDate = new Date(value);
          const today = new Date();
          const age = today.getFullYear() - birthDate.getFullYear();
          if (age < 13 || age > 120) {
            errors.dateOfBirth = t('validation.invalidAge', 'Yaş 13-120 arasında olmalıdır');
          }
        }
        break;
        
      default:
        break;
    }
    
    return errors;
  }, []);

  // Input change handler with real-time validation
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeInput(value);
    
    setFormData(prev => ({ ...prev, [name]: sanitizedValue }));
    setTouchedFields(prev => new Set([...prev, name]));
    
    // Real-time validation
    const fieldErrors = validateField(name, sanitizedValue);
    setValidationErrors(prev => ({ 
      ...prev, 
      ...fieldErrors,
      ...(Object.keys(fieldErrors).length === 0 ? { [name]: undefined } : {})
    }));
  }, [validateField]);

  // Array field management
  const handleArrayAdd = useCallback((fieldName, newItem) => {
    if (newItem.trim()) {
      setFormData(prev => ({
        ...prev,
        [fieldName]: [...(prev[fieldName] || []), sanitizeInput(newItem.trim())]
      }));
    }
  }, []);
  const handleArrayRemove = useCallback((fieldName, index) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: prev[fieldName].filter((_, i) => i !== index)
    }));
  }, []);

  // Profile photo upload handlers
  const handlePhotoClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handlePhotoChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setValidationErrors(prev => ({
          ...prev,
          profilePhoto: 'Lütfen geçerli bir resim dosyası seçin (JPEG, PNG, WebP)'
        }));
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        setValidationErrors(prev => ({
          ...prev,
          profilePhoto: 'Dosya boyutu 5MB\'dan küçük olmalıdır'
        }));
        return;
      }

      // Clear any previous errors
      setValidationErrors(prev => ({
        ...prev,
        profilePhoto: undefined
      }));

      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target.result;
        setPhotoPreview(base64String);
        setFormData(prev => ({
          ...prev,
          profilePhoto: base64String
        }));
        setTouchedFields(prev => new Set([...prev, 'profilePhoto']));
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const handlePhotoRemove = useCallback(() => {
    setPhotoPreview(null);
    setFormData(prev => ({
      ...prev,
      profilePhoto: null
    }));
    setTouchedFields(prev => new Set([...prev, 'profilePhoto']));
    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // Emergency contact management
  const handleEmergencyContactChange = useCallback((index, field, value) => {
    const sanitizedValue = sanitizeInput(value);
    setFormData(prev => ({
      ...prev,
      emergencyContacts: prev.emergencyContacts.map((contact, i) => 
        i === index ? { ...contact, [field]: sanitizedValue } : contact
      )
    }));
  }, []);

  const addEmergencyContact = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      emergencyContacts: [
        ...prev.emergencyContacts,
        { name: '', phone: '', relationship: '', location: '', isPrimary: false }
      ]
    }));
  }, []);

  const removeEmergencyContact = useCallback((index) => {
    setFormData(prev => ({
      ...prev,
      emergencyContacts: prev.emergencyContacts.filter((_, i) => i !== index)
    }));
  }, []);  // Enhanced save handler with comprehensive validation and error handling
  const handleSave = useCallback(async () => {
    if (isLoading) {
      console.log('🔄 ProfileEditScreen: Save already in progress, ignoring request');
      return;
    }

    setIsLoading(true);
    setValidationErrors({});
    
    try {
      console.log('🔄 ProfileEditScreen: Starting profile save process');
      console.log('📝 ProfileEditScreen: Form data to save:', formData);
      
      // Comprehensive data validation
      const validation = validateProfileData(formData);
      
      if (validation.errors && Object.keys(validation.errors).length > 0) {
        console.warn('❌ ProfileEditScreen: Validation failed:', validation.errors);
        setValidationErrors(validation.errors);
        
        // Enhanced error feedback
        const firstErrorField = Object.keys(validation.errors)[0];
        const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(() => errorElement.focus(), 300);
        }
        
        // Show validation error toast
        setShowSaveError(true);
        setTimeout(() => setShowSaveError(false), 3000);
        
        setIsLoading(false);
        return;
      }

      // Prepare optimized update data
      const updateData = {
        // Core identity fields
        name: sanitizeInput(formData.name) || user?.name,
        username: sanitizeInput(formData.username) || user?.username,
        bio: sanitizeInput(formData.bio) || user?.bio,
        dateOfBirth: formData.dateOfBirth || user?.dateOfBirth,
        gender: formData.gender || user?.gender,
        nationality: formData.nationality || user?.nationality,
        
        // Physical characteristics
        height: formData.height || user?.height,
        weight: formData.weight || user?.weight,
        bloodType: formData.bloodType || user?.bloodType,
        eyeColor: formData.eyeColor || user?.eyeColor,
        hairColor: formData.hairColor || user?.hairColor,
        
        // Contact information
        phone: formData.phone ? formatPhoneNumber(formData.phone) : user?.phone,
        email: formData.email || user?.email,
        secondaryEmail: formData.secondaryEmail || user?.secondaryEmail,
        
        // Address (preserve nested structure)
        address: {
          ...(user?.address || {}),
          ...(formData.address || {}),
          updatedAt: new Date().toISOString()
        },
          // Health data - Fixed field mappings to match ProfileScreen
        allergies: Array.isArray(formData.allergies) ? formData.allergies : (user?.allergies || []),
        medications: Array.isArray(formData.medications) ? formData.medications : (user?.medications || []),
        conditions: Array.isArray(formData.medicalConditions) ? formData.medicalConditions : (user?.medicalConditions || user?.conditions || []),
        medicalConditions: Array.isArray(formData.medicalConditions) ? formData.medicalConditions : (user?.medicalConditions || []),
        chronicDiseases: Array.isArray(formData.chronicDiseases) ? formData.chronicDiseases : (user?.chronicDiseases || []),
          // Emergency contacts with validation - Fixed field mapping for ProfileScreen compatibility
        emergencyContacts: (formData.emergencyContacts || []).map(contact => ({
          name: sanitizeInput(contact.name),
          phone: contact.phone ? formatPhoneNumber(contact.phone) : contact.phone,
          relationship: sanitizeInput(contact.relationship),
          relation: sanitizeInput(contact.relationship), // ProfileScreen expects 'relation' field
          location: sanitizeInput(contact.location || ''),
          updatedAt: new Date().toISOString()
        })),
        
        // Professional info
        languages: Array.isArray(formData.languages) ? formData.languages : (user?.languages || []),
        occupation: sanitizeInput(formData.occupation) || user?.occupation,
        workplace: sanitizeInput(formData.workplace) || user?.workplace,
        
        // Profile photo
        profilePhoto: formData.profilePhoto || user?.profilePhoto,
        
        // Metadata
        updatedAt: new Date().toISOString(),
        editedSections: editSection ? [editSection] : ['general'],
        lastEditedBy: 'user'
      };

      console.log('📤 ProfileEditScreen: Sending update data:', updateData);

      // Execute profile update
      const updatedUser = await updateUserProfile(updateData);
      
      if (!updatedUser) {
        throw new Error('Profile update returned no data');
      }

      // Clear validation errors
      setValidationErrors({});
      
      // Success feedback
      setShowSaveSuccess(true);
      console.log('✅ ProfileEditScreen: Profile saved successfully');
      
      // Enhanced navigation with context preservation
      setTimeout(() => {
        setShowSaveSuccess(false);
        
        // Determine navigation target based on edit section
        const navigationTarget = screenParams?.returnTo || 'profile';
        const navigationParams = editSection ? { 
          tab: editSection === 'emergency' ? 'identity' : 'identity',
          scrollTo: editSection,
          timestamp: Date.now()
        } : {};
        
        console.log('🔄 ProfileEditScreen: Navigating to:', navigationTarget, navigationParams);
        
        if (Object.keys(navigationParams).length > 0) {
          navigateToScreen(navigationTarget, navigationParams);
        } else {
          navigateToScreen(navigationTarget);
        }
      }, 1000);
      
    } catch (error) {
      console.error('❌ ProfileEditScreen: Save failed:', error);
      
      // Enhanced error handling with user-friendly messages
      let errorMessage = 'Profil kaydedilirken bir hata oluştu.';
      
      if (error.message.includes('validation')) {
        errorMessage = 'Girilen bilgilerde hata var. Lütfen kontrol edin.';
      } else if (error.message.includes('network')) {
        errorMessage = 'Bağlantı hatası. Lütfen tekrar deneyin.';
      } else if (error.message.includes('permission')) {
        errorMessage = 'Bu işlem için yetkiniz yok.';
      }
      
      // Show error feedback
      setShowSaveError(true);
      setTimeout(() => setShowSaveError(false), 4000);
      
      // Update validation errors if available
      if (error.validationErrors) {
        setValidationErrors(error.validationErrors);
      }
        } finally {
      setIsLoading(false);
    }
  }, [
    formData, 
    user, 
    updateUserProfile, 
    navigateToScreen, 
    editSection, 
    screenParams,
    isLoading
  ]);// Tab configuration - Reorganized for better UX with merged health & safety section
  const tabs = useMemo(() => {
    // Safety check for t function
    if (!t) return [];
    
    return [
      { 
        id: 'general', 
        label: t('profile.general', 'Genel Bilgiler'), 
        icon: <User size={16} />,
        color: 'blue'
      },
      { 
        id: 'physical', 
        label: t('profile.physical', 'Fiziksel Bilgiler'), 
        icon: <Activity size={16} />,
        color: 'purple'
      },
      { 
        id: 'emergency', 
        label: t('profile.emergency', 'Acil Durum'), 
        icon: <Shield size={16} />,
        color: 'red'
      },
      { 
        id: 'medical', 
        label: t('profile.medical', 'Tıbbi Bilgiler'), 
        icon: <Heart size={16} />,
        color: 'pink'
      }
    ];
  }, [t]);
  // Set initial tab
  useEffect(() => {
    console.log('🔄 ProfileEditScreen: Setting active tab from editSection:', editSection);
    setActiveTab(editSection);
  }, [editSection]);

  // Tab navigation
  const renderTabNavigation = () => (
    <div className={`tab-nav flex overflow-x-auto border-b ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'} shadow-sm`}>
      {tabs.map(tab => (
        <button
          key={tab.id}
          onClick={() => setActiveTab(tab.id)}
          className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-all duration-200
            ${activeTab === tab.id
              ? `border-b-2 border-${tab.color}-500 ${isDarkMode ? `text-${tab.color}-400 bg-gray-700` : `text-${tab.color}-600 bg-${tab.color}-50`}`
              : `${isDarkMode ? 'text-gray-300 hover:text-white hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`
            }`}
        >
          {tab.icon}
          <span>{tab.label}</span>
        </button>
      ))}
    </div>  );

  // Form sections
  const renderGeneralForm = () => (
    <div className="space-y-6 tab-content">
      {/* Profile Picture Section */}
      <div className="flex justify-center">
        <div className="relative group">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handlePhotoChange}
            className="hidden"
          />
          
          {/* Profile photo display */}
          <div 
            onClick={handlePhotoClick}
            className={`relative w-32 h-32 rounded-full overflow-hidden cursor-pointer transition-all duration-300 group-hover:scale-105
              ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-200 border-gray-300'} 
              border-4 shadow-lg`}
          >
            {photoPreview ? (
              <img 
                src={photoPreview} 
                alt="Profile" 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className={`w-full h-full flex items-center justify-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <User size={32} />
              </div>
            )}
            
            {/* Overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-white">
                <Camera size={24} />
              </div>
            </div>
          </div>
          
          {/* Remove photo button */}
          {photoPreview && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlePhotoRemove();
              }}
              className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors shadow-lg"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>
      
      {validationErrors.profilePhoto && (
        <p className="text-red-500 text-sm text-center flex items-center justify-center">
          <AlertCircle size={14} className="mr-1" />
          {validationErrors.profilePhoto}
        </p>
      )}

      {/* Basic Information */}
      <div className={`identity-card p-6 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
        <h3 className={`text-lg font-semibold mb-4 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          <User size={20} className="mr-2" />
          {t('profile.basicInfo', 'Temel Bilgiler')}
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ValidatedInput
            label={t('profile.name', 'İsim')}
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Ahmet Yılmaz"
            isDarkMode={isDarkMode}
            required
            maxLength={50}
            error={validationErrors.name}
          />

          <ValidatedInput
            label={t('profile.username', 'Kullanıcı Adı')}
            name="username"
            value={formData.username}
            onChange={handleInputChange}
            placeholder="@ahmetyilmaz"
            isDarkMode={isDarkMode}
            maxLength={20}
            error={validationErrors.username}
          />

          <div className="md:col-span-2">
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {t('profile.bio', 'Hakkında')}
            </label>
            <textarea
              name="bio"
              value={formData.bio}
              onChange={handleInputChange}
              placeholder={t('profile.bioPlaceholder', 'Kendiniz hakkında kısa bir açıklama yazın...')}
              maxLength={500}
              rows={3}
              className={`field-input w-full p-3 rounded-xl resize-none ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
            />
            <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {formData.bio.length}/500 karakter
            </p>
          </div>

          <ValidatedInput
            label={t('profile.dateOfBirth', 'Doğum Tarihi')}
            name="dateOfBirth"
            type="date"
            value={formData.dateOfBirth}
            onChange={handleInputChange}
            isDarkMode={isDarkMode}
            error={validationErrors.dateOfBirth}
          />

          <div>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {t('profile.gender', 'Cinsiyet')}
            </label>
            <select
              name="gender"
              value={formData.gender}
              onChange={handleInputChange}
              className={`field-input w-full p-3 rounded-xl ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
            >
              <option value="">{t('common.select', 'Seçiniz')}</option>
              <option value="male">{t('profile.male', 'Erkek')}</option>
              <option value="female">{t('profile.female', 'Kadın')}</option>
              <option value="other">{t('profile.other', 'Diğer')}</option>
              <option value="prefer_not_to_say">{t('profile.preferNotToSay', 'Belirtmek İstemiyorum')}</option>
            </select>
          </div>

          <ValidatedInput
            label={t('profile.nationality', 'Uyruk')}
            name="nationality"
            value={formData.nationality}
            onChange={handleInputChange}
            placeholder="Türk"
            isDarkMode={isDarkMode}
            error={validationErrors.nationality}
          />
        </div>
      </div>

      {/* Contact Information */}
      <div className={`identity-card p-6 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
        <h3 className={`text-lg font-semibold mb-4 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          <Phone size={20} className="mr-2" />
          {t('profile.contactInfo', 'İletişim Bilgileri')}
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ValidatedInput
            label={t('profile.phone', 'Telefon')}
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleInputChange}
            placeholder="+90 555 123 4567"
            isDarkMode={isDarkMode}
            error={validationErrors.phone}
          />

          <ValidatedInput
            label={t('profile.email', 'E-posta')}
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="ahmet@example.com"
            isDarkMode={isDarkMode}
            error={validationErrors.email}
          />

          <ValidatedInput
            label={t('profile.secondaryEmail', 'İkinci E-posta')}
            name="secondaryEmail"
            type="email"
            value={formData.secondaryEmail}
            onChange={handleInputChange}
            placeholder="ahmet.backup@example.com"
            isDarkMode={isDarkMode}
            error={validationErrors.secondaryEmail}
          />
        </div>
      </div>      {/* Address Information */}
      <div className={`identity-card p-6 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
        <h3 className={`text-lg font-semibold mb-4 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          <MapPin size={20} className="mr-2" />
          {t('profile.addressInfo', 'Adres Bilgileri')}
        </h3>
        
        <InternationalAddressInput
          value={formData.address}
          onChange={(address) => setFormData(prev => ({ ...prev, address }))}
          isDarkMode={isDarkMode}
          error={validationErrors}
          label={t('profile.addressInfo', 'Adres Bilgileri')}
        />
      </div>

      {/* Personal Interests */}
      <ArrayFieldManager
        title={t('profile.languages', 'Diller')}
        items={formData.languages || []}
        onAdd={(language) => handleArrayAdd('languages', language)}
        onRemove={(index) => handleArrayRemove('languages', index)}
        placeholder={t('profile.languagePlaceholder', 'Dil ekle (örn: Türkçe, İngilizce)')}
        isDarkMode={isDarkMode}
        addButtonColor="purple"
        emptyMessage={t('profile.noLanguages', 'Henüz dil eklenmemiş')}
        t={t}
      />

      <ArrayFieldManager
        title={t('profile.hobbies', 'Hobiler')}
        items={formData.hobbies || []}
        onAdd={(hobby) => handleArrayAdd('hobbies', hobby)}
        onRemove={(index) => handleArrayRemove('hobbies', index)}
        placeholder={t('profile.hobbyPlaceholder', 'Hobi ekle')}
        isDarkMode={isDarkMode}
        addButtonColor="green"
        emptyMessage={t('profile.noHobbies', 'Henüz hobi eklenmemiş')}
        t={t}
      />
    </div>
  );

  const renderPhysicalForm = () => (
    <div className="space-y-6 tab-content">
      {/* Physical Characteristics */}
      <div className={`identity-card p-6 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
        <h3 className={`text-lg font-semibold mb-4 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          <Activity size={20} className="mr-2" />
          {t('profile.physicalInfo', 'Fiziksel Bilgiler')}
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ValidatedInput
            label={t('profile.height', 'Boy (cm)')}
            name="height"
            type="number"
            value={formData.height}
            onChange={handleInputChange}
            placeholder="175"
            isDarkMode={isDarkMode}
            error={validationErrors.height}
          />

          <ValidatedInput
            label={t('profile.weight', 'Kilo (kg)')}
            name="weight"
            type="number"
            value={formData.weight}
            onChange={handleInputChange}
            placeholder="70"
            isDarkMode={isDarkMode}
            error={validationErrors.weight}
          />

          <div>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {t('profile.bloodType', 'Kan Grubu')}
            </label>
            <select
              name="bloodType"
              value={formData.bloodType}
              onChange={handleInputChange}
              className={`field-input w-full p-3 rounded-xl ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
            >
              <option value="">{t('common.select', 'Seçiniz')}</option>
              <option value="A+">A Rh+</option>
              <option value="A-">A Rh-</option>
              <option value="B+">B Rh+</option>
              <option value="B-">B Rh-</option>
              <option value="AB+">AB Rh+</option>
              <option value="AB-">AB Rh-</option>
              <option value="O+">O Rh+</option>
              <option value="O-">O Rh-</option>
            </select>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {t('profile.eyeColor', 'Göz Rengi')}
            </label>
            <select
              name="eyeColor"
              value={formData.eyeColor}
              onChange={handleInputChange}
              className={`field-input w-full p-3 rounded-xl ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
            >
              <option value="">{t('common.select', 'Seçiniz')}</option>
              <option value="brown">{t('profile.brown', 'Kahverengi')}</option>
              <option value="green">{t('profile.green', 'Yeşil')}</option>
              <option value="blue">{t('profile.blue', 'Mavi')}</option>
              <option value="hazel">{t('profile.hazel', 'Ela')}</option>
              <option value="gray">{t('profile.gray', 'Gri')}</option>
              <option value="black">{t('profile.black', 'Siyah')}</option>
            </select>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {t('profile.hairColor', 'Saç Rengi')}
            </label>
            <select
              name="hairColor"
              value={formData.hairColor}
              onChange={handleInputChange}
              className={`field-input w-full p-3 rounded-xl ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
            >
              <option value="">{t('common.select', 'Seçiniz')}</option>
              <option value="black">{t('profile.black', 'Siyah')}</option>
              <option value="brown">{t('profile.brown', 'Kahverengi')}</option>
              <option value="blonde">{t('profile.blonde', 'Sarı')}</option>
              <option value="red">{t('profile.red', 'Kızıl')}</option>
              <option value="gray">{t('profile.gray', 'Gri')}</option>
              <option value="white">{t('profile.white', 'Beyaz')}</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderEmergencyForm = () => (
    <div className="space-y-6 tab-content">
      {/* Emergency Contacts Header */}
      <div className="flex justify-between items-center">
        <h2 className={`text-xl font-bold flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          <Shield size={24} className="mr-2 text-red-500" />
          {t('profile.emergencyContacts', 'Acil Durum Kişileri')}
        </h2>
        <button
          onClick={addEmergencyContact}
          className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
        >
          <Plus size={16} />
          <span>{t('profile.addContact', 'Kişi Ekle')}</span>
        </button>
      </div>

      {/* Emergency Contacts List */}
      <div className="space-y-4">
        {formData.emergencyContacts.length === 0 ? (
          <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <Contact size={48} className="mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">{t('profile.noEmergencyContacts', 'Henüz acil durum kişisi eklenmemiş')}</p>
            <p className="text-sm mt-2">{t('profile.emergencyContactHint', 'Acil durumlar için ulaşılabilecek kişileri ekleyin')}</p>
          </div>
        ) : (
          formData.emergencyContacts.map((contact, index) => (
            <EmergencyContactCard
              key={index}
              contact={contact}
              index={index}
              onChange={handleEmergencyContactChange}
              onRemove={removeEmergencyContact}
              isDarkMode={isDarkMode}
              canRemove={formData.emergencyContacts.length > 1}
              validationErrors={validationErrors}
              t={t}
            />
          ))
        )}
      </div>
    </div>
  );

  const renderMedicalForm = () => (
    <div className="space-y-6 tab-content">
      {/* Health Manager */}
      <HealthManager
        allergies={formData.allergies || []}
        medicalConditions={formData.medicalConditions || []}
        medications={formData.medications || []}
        chronicDiseases={formData.chronicDiseases || []}
        onUpdate={(healthData) => {
          setFormData(prev => ({
            ...prev,
            ...healthData
          }));
        }}
        isDarkMode={isDarkMode}
        t={t}
      />

      {/* Additional Notes */}
      <div className={`identity-card p-6 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
        <h3 className={`text-lg font-semibold mb-4 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          <FileText size={20} className="mr-2" />
          {t('profile.additionalNotes', 'Ek Notlar')}
        </h3>
        
        <textarea
          name="notes"
          value={formData.notes}
          onChange={handleInputChange}
          placeholder={t('profile.notesPlaceholder', 'Önemli notlar, özel durumlar, ilaç alerjileri vb...')}
          maxLength={1000}
          rows={5}
          className={`field-input w-full p-3 rounded-xl resize-none ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
        />
        <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {formData.notes.length}/1000 karakter
        </p>
      </div>
    </div>
  );

  // Main render
  return (
    <div className={`identity-edit-container min-h-screen pb-20 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`sticky top-0 z-30 ${isDarkMode ? 'bg-gray-900' : 'bg-white'} border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} shadow-sm`}>
        <div className="flex items-center justify-between p-4">
          <button
            onClick={() => navigateToScreen('profile')}
            className={`flex items-center space-x-2 ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors`}
          >
            <ArrowLeft size={20} />
            <span className="font-medium">{t('common.back', 'Geri')}</span>
          </button>
          
          <h1 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {t('profile.editProfile', 'Kimlik Düzenle')}
          </h1>
          
          <div className="w-16" /> {/* Spacer for centering */}
        </div>
      </div>

      {/* Tab Navigation */}
      {renderTabNavigation()}

      {/* Content */}
      <div className="p-4">
        {/* General Error Display */}
        {validationErrors.general && (
          <div className={`mb-4 p-4 rounded-xl border-l-4 border-red-500 ${isDarkMode ? 'bg-red-900/20 text-red-300' : 'bg-red-50 text-red-700'}`}>
            <div className="flex items-center">
              <AlertCircle size={16} className="mr-2" />
              <span>{validationErrors.general}</span>
            </div>
          </div>
        )}

        {/* Tab Content */}
        {activeTab === 'general' && renderGeneralForm()}
        {activeTab === 'physical' && renderPhysicalForm()}
        {activeTab === 'emergency' && renderEmergencyForm()}
        {activeTab === 'medical' && renderMedicalForm()}
      </div>

      {/* Fixed Save Button */}
      <div className={`fixed bottom-0 left-0 right-0 p-4 ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'} border-t shadow-lg`}>
        <button
          onClick={handleSave}
          disabled={isLoading}
          className={`w-full flex items-center justify-center space-x-2 py-4 px-6 rounded-xl font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed
            ${isDarkMode 
              ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white' 
              : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white'
            } shadow-lg`}
        >
          {isLoading ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>{t('common.saving', 'Kaydediliyor...')}</span>
            </>
          ) : (
            <>
              <Save size={20} />
              <span>{t('common.save', 'Değişiklikleri Kaydet')}</span>
            </>
          )}
        </button>
      </div>

      {/* Success Notification */}
      {showSaveSuccess && (
        <div className="fixed top-20 left-0 right-0 flex justify-center z-50">
          <div className={`save-success px-6 py-3 rounded-xl shadow-xl flex items-center space-x-2
            ${isDarkMode ? 'bg-green-900 text-green-100 border border-green-700' : 'bg-green-100 text-green-800 border border-green-200'}`}>
            <CheckCircle size={16} />
            <span>{t('profile.saveSuccess', 'Kimlik bilgileri başarıyla güncellendi!')}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileEditScreen;
